import { supabase } from './supabase';

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY || '';

const IOS_REGEX = /iphone|ipad|ipod/i;

export function isIosSafari(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const isIOS = IOS_REGEX.test(ua);
  const isSafari =
    /safari/i.test(ua) && !/crios|fxios|OPiOS|mercury/i.test(ua || '');
  return isIOS && isSafari;
}

export function isStandalonePwa(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    Boolean(window.matchMedia?.('(display-mode: standalone)').matches) ||
    (window.navigator as any)?.standalone === true
  );
}

export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (typeof Notification === 'undefined') {
    return 'denied';
  }

  if (Notification.permission === 'granted') {
    return 'granted';
  }

  if (Notification.permission === 'denied') {
    return 'denied';
  }

  return Notification.requestPermission();
}

export async function getServiceWorkerRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (typeof navigator === 'undefined' || !navigator.serviceWorker) {
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    return registration;
  } catch (err) {
    console.error('Failed to resolve service worker registration', err);
    return null;
  }
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const normalized =
    base64String.replace(/\s+/g, '').replace(/-/g, '+').replace(/_/g, '/');
  const padding = '='.repeat(
    (4 - (normalized.length % 4)) % 4
  );

  try {
    const rawData = window.atob(normalized + padding);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  } catch (error) {
    console.error('Failed to decode VAPID key', error);
    throw new Error(
      'Push notification setup failed because the configured VAPID key is invalid.'
    );
  }
}

export async function createAndStorePushSubscription(userId: string) {
  if (!VAPID_PUBLIC_KEY) {
    throw new Error(
      'VAPID public key missing. Set VITE_VAPID_PUBLIC_KEY in your env.'
    );
  }

  const permission = await requestNotificationPermission();
  if (permission !== 'granted') {
    return { permission };
  }

  const registration = await getServiceWorkerRegistration();
  if (!registration) {
    throw new Error(
      'Service worker registration missing. Ensure /sw.js is registered.'
    );
  }

  let subscription = (await registration.pushManager.getSubscription()) ?? null;

  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(
        VAPID_PUBLIC_KEY
      ) as unknown as any,
    });
  }

  const payload = subscription.toJSON();
  const { error } = await supabase.from('user_push_subscriptions').upsert(
    {
      user_id: userId,
      endpoint: subscription.endpoint,
      subscription: payload,
      keys: payload.keys ?? null,
      is_ios_pwa: isIosSafari() ? isStandalonePwa() : false,
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
      updated_at: new Date().toISOString(),
    },
    { onConflict: 'user_id,endpoint' }
  );

  if (error) {
    throw error;
  }

  return { permission, subscription };
}
